import { useEffect, useState } from 'react';
import type { PlantillaDocumento, VersionPlantilla } from '../../../api/ops/servicios';
import { ErrorApi } from '../../../api/problema';
import { Ayuda } from '../../../componentes/Ayuda';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { Desplegable } from '../../../componentes/Desplegable';
import { Icono } from '../../../componentes/Icono';
import type { NombreIcono } from '../../../componentes/iconos';
import { Interruptor } from '../../../componentes/Interruptor';
import { OpcionRadio } from '../../../componentes/OpcionRadio';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Ventana } from '../../../componentes/Ventana';
import { diaMes } from '../../../logica/fechasCortas';
import type { PropsPantalla } from '../../tipos';
import { textoDeError } from '../../agenda/logica';
import type { DatosServicios } from '../datos';
import { iconoCategoria, textoDuracion } from '../logica';
import { AYUDA } from '../textos';
import { EJEMPLOS, VALIDEZ, VARIABLES, erroresPlantilla, formDe, formVacio, lineaFirma, serviciosQueLaPiden, versionDe, vistaPrevia, type FormPlantilla, type PestanaPlantilla } from './logica';

interface Props extends PropsPantalla {
    datos: DatosServicios;
    plantilla: PlantillaDocumento | null;
    pestana: PestanaPlantilla;
    gestiona: boolean;
    onPestana: (p: PestanaPlantilla) => void;
    onCerrar: () => void;
    onCreada: (t: PlantillaDocumento) => void;
    onCambiada: (t: PlantillaDocumento) => void;
}

const PESTANAS: readonly [PestanaPlantilla, string, NombreIcono][] = [
    ['texto', 'Texto', 'file-text'],
    ['vista', 'Vista previa', 'eye'],
    ['servicios', 'Servicios', 'scissors'],
    ['versiones', 'Versiones', 'history'],
];

/**
 * Plantilla de consentimiento (9.11) en RV Panel de ficha: Texto · Vista previa (nueva) y además Servicios · Versiones
 * (abierta). El texto lleva los datos que Citas rellena (G484); publicar crea una versión nueva e inmutable
 * (`publishDocumentTemplateVersion`) y lo firmado conserva la suya; el nombre se cambia aparte con `If-Match`. La vista
 * previa la rellena la API con datos de ejemplo (`previewDocumentTemplate`); la nueva, el panel con los mismos ejemplos.
 * Archivar una plantilla que piden servicios no se deja: antes hay que quitarla de ellos.
 */
export function FichaPlantilla(props: Props) {
    const { ops, negocio, movil, datos, plantilla, pestana, gestiona, onPestana, onCerrar, onCreada, onCambiada, navegar } = props;
    const avisar = useAvisoBreve();
    const nueva = plantilla === null;
    const archivada = plantilla?.status === 'archived';
    const [version, setVersion] = useState<string | null>(null);
    const [form, setForm] = useState<FormPlantilla>(() => (plantilla === null ? formVacio() : formDe(plantilla)));
    const [probado, setProbado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [usada, setUsada] = useState(false);

    useEffect(() => {
        if (plantilla === null) {
            return;
        }
        let vivo = true;
        ops.getDocumentTemplate(negocio, plantilla.id)
            .then((r) => {
                if (vivo) {
                    setVersion(r.version);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
        // Solo al abrir: después, la versión la da cada respuesta.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, negocio, plantilla?.id]);

    const e = probado ? erroresPlantilla(form) : {};
    const pon =
        <K extends keyof FormPlantilla>(k: K) =>
        (v: FormPlantilla[K]) => {
            setForm((f) => ({ ...f, [k]: v }));
        };
    const fallo = (err: unknown) => {
        avisar({ tipo: 'error', texto: err instanceof ErrorApi && err.estado === 412 ? 'Alguien la ha cambiado mientras tanto. Cierra la ficha y vuelve a abrirla.' : textoDeError(err) });
    };

    const guardar = async () => {
        setProbado(true);
        if (Object.keys(erroresPlantilla(form)).length > 0) {
            if (pestana !== 'texto') {
                onPestana('texto');
            }
            return;
        }
        setOcupado(true);
        try {
            if (plantilla === null) {
                const r = await ops.createDocumentTemplate(negocio, { name: form.nombre.trim(), kind: 'consent', first_version: versionDe(form) });
                avisar({ tipo: 'ok', texto: `Plantilla «${r.datos.name}» creada. Elígela en la ficha de los servicios que la piden.` });
                onCreada(r.datos);
                return;
            }
            let v = version;
            if (form.nombre.trim() !== plantilla.name && v !== null) {
                const r = await ops.updateDocumentTemplate(negocio, plantilla.id, { name: form.nombre.trim() }, v);
                v = r.version;
            }
            const publicada = await ops.publishDocumentTemplateVersion(negocio, plantilla.id, versionDe(form));
            const r = await ops.getDocumentTemplate(negocio, plantilla.id);
            setVersion(r.version ?? v);
            onCambiada(r.datos);
            avisar({ tipo: 'ok', texto: `Versión ${String(publicada.number)} publicada. Lo ya firmado conserva su versión.` });
        } catch (err) {
            fallo(err);
        } finally {
            setOcupado(false);
        }
    };

    const archivar = () => {
        if (plantilla === null || version === null) {
            return;
        }
        if (serviciosQueLaPiden(plantilla, datos.servicios).length > 0) {
            setUsada(true);
            return;
        }
        setOcupado(true);
        ops.archiveDocumentTemplate(negocio, plantilla.id, version)
            .then((r) => {
                avisar({ tipo: 'ok', texto: `Plantilla «${r.datos.name}» archivada.` });
                onCambiada(r.datos);
            })
            .catch(fallo)
            .finally(() => { setOcupado(false); });
    };

    const pestanas = nueva ? PESTANAS.slice(0, 2) : PESTANAS;
    const v = plantilla?.current_version;
    const usos = plantilla === null ? [] : serviciosQueLaPiden(plantilla, datos.servicios);
    const accion = gestiona && !archivada && pestana === 'texto' ? (nueva ? 'Crear plantilla' : `Publicar versión ${String((v?.number ?? 0) + 1)}`) : '';

    const contenido = (() => {
        if (pestana === 'vista') {
            return <Vista {...props} form={form} />;
        }
        if (pestana === 'servicios' && plantilla !== null) {
            return (
                <div className="rv-sv-form">
                    {usos.length === 0 ? (
                        <p className="rv-sv-nota">Ningún servicio la pide todavía.</p>
                    ) : (
                        usos.map((s) => (
                            <div key={s.id} className="rv-sv-quien">
                                <span className="rv-sv-baldosa" style={{ background: s.current_version.color ?? 'var(--rv-ink)' }}>
                                    <Icono nombre={iconoCategoria(s.current_version.category)} />
                                </span>
                                <span className="rv-sv-celda__texto">
                                    <strong>{s.current_version.name}</strong>
                                    <small>{[s.current_version.category ?? '', textoDuracion(s.current_version.base_duration_minutes)].filter((x) => x !== '').join(' · ')}</small>
                                </span>
                                <Boton tipo="secundario" icono="arrow-right" onClick={() => { navegar(`/servicios?servicio=${encodeURIComponent(s.id)}&tab=datos`); }}>
                                    Abrir el servicio
                                </Boton>
                            </div>
                        ))
                    )}
                    <p className="rv-sv-nota">Para que un servicio la pida, elígela en su ficha, en «Consentimiento que firma el cliente».</p>
                </div>
            );
        }
        if (pestana === 'versiones' && plantilla !== null) {
            return <Versiones {...props} plantilla={plantilla} />;
        }
        const editable = gestiona && !archivada;
        return (
            <div className="rv-sv-form" style={{ maxWidth: 640 }}>
                {archivada && (
                    <p className="rv-sv-nota">
                        <Icono nombre="eye" />
                        <span>Archivada: ya no se puede pedir. Lo firmado con ella se conserva.</span>
                    </p>
                )}
                <fieldset disabled={!editable} style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
                    <span className="rv-sv-grupo__t">El documento</span>
                    <Campo etiqueta="Nombre de la plantilla" valor={form.nombre} onChange={pon('nombre')} error={e.nombre ?? null} ayuda="Solo lo ve tu equipo." max={120} mayusculas="sentences" />
                    <Campo etiqueta="Título que ve el cliente" valor={form.titulo} onChange={pon('titulo')} error={e.titulo ?? null} max={200} mayusculas="sentences" />
                    <Campo etiqueta="Texto del consentimiento" filas={movil ? 10 : 12} valor={form.texto} onChange={pon('texto')} error={e.texto ?? null} max={50000} />
                    <div className="rv-sv-grupo">
                        <span className="rv-sv-grupo__t">Datos que se rellenan solos</span>
                        <div className="rv-sv-variables">
                            {VARIABLES.map((x) => (
                                <Boton key={x.nombre} tipo="secundario" icono="plus" onClick={() => { pon('texto')(`${form.texto.replace(/\s*$/, '')} {{${x.nombre}}}`); }}>
                                    {x.texto}
                                </Boton>
                            ))}
                        </div>
                        <span className="rv-sv-nota">Tócalos para añadirlos al final del texto. Al pedir la firma se cambian por los datos de esa cita.</span>
                    </div>
                    <span className="rv-sv-grupo__t">Cómo se firma</span>
                    <div role="radiogroup" aria-label="Nivel de firma" className="rv-sv-grupo">
                        <OpcionRadio elegida={form.nivel === 'advanced'} titulo="Avanzada" texto="La habitual para consentimientos: identifica a quien firma." onElegir={() => { pon('nivel')('advanced'); }} />
                        <OpcionRadio elegida={form.nivel === 'qualified'} bloqueada titulo="Cualificada" texto="Con certificado. Tu proveedor de firma no la ofrece ahora." onElegir={() => undefined} />
                    </div>
                    <Interruptor etiqueta="Verificar su identidad antes de firmar" ayuda="El proveedor de firma comprueba su DNI o su certificado." valor={form.identidad} onChange={pon('identidad')} />
                    <Desplegable etiqueta="Lo firmado vale" valor={form.vale} opciones={VALIDEZ} onChange={pon('vale')} ayuda="Mientras valga, no se le vuelve a pedir en sus próximas citas." />
                </fieldset>
                {!nueva && editable && (
                    <div className="rv-sv-pie">
                        <span>{`Publicar crea la versión ${String((v?.number ?? 0) + 1)}. Lo firmado con la ${String(v?.number ?? 0)} la conserva.`}</span>
                        <Boton tipo="secundario" rojo icono="archive" onClick={archivar}>
                            Archivar la plantilla
                        </Boton>
                    </div>
                )}
            </div>
        );
    })();

    return (
        <>
            <PanelLateral
                abierto
                onCerrar={onCerrar}
                movil={movil}
                tamano="ficha"
                eyebrow={nueva ? 'Nueva plantilla' : 'Plantilla de consentimiento'}
                titulo={nueva ? (form.nombre.trim() === '' ? 'Nueva plantilla' : form.nombre.trim()) : plantilla.name}
                subtitulo={nueva ? 'Consentimiento que firma el cliente' : `Versión ${String(v?.number ?? 1)} · ${usos.length === 0 ? 'ningún servicio la pide' : `la piden ${String(usos.length)} ${usos.length === 1 ? 'servicio' : 'servicios'}`}`}
                {...(archivada ? { chip: 'Archivada', chipEstado: 'neutral' as const } : {})}
                icono="file-text"
                accion={accion}
                accionIcono={nueva ? 'plus' : 'check'}
                ocupado={ocupado}
                inactiva={!nueva && version === null}
                onAccion={() => { void guardar(); }}
            >
                <div className="rv-sv-ficha">
                    <div className="rv-sv-botonera" role="tablist" aria-label="Apartados de la plantilla">
                        {pestanas.map(([clave, texto, icono]) => (
                            <button key={clave} type="button" role="tab" aria-selected={pestana === clave} className="rv-sv-botonera__b" onClick={() => { onPestana(clave); }}>
                                <Icono nombre={icono} />
                                <span>{texto}</span>
                            </button>
                        ))}
                    </div>
                    <div className="rv-sv-ficha__contenido">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <h3 className="rv-sv-ceja">{pestanas.find(([x]) => x === pestana)?.[1] ?? ''}</h3>
                            <span style={{ flex: 1 }} />
                            <Ayuda tarjetas={pestana === 'vista' ? AYUDA.tplPrev : AYUDA.tpl} />
                        </div>
                        {contenido}
                    </div>
                </div>
            </PanelLateral>
            <Ventana
                abierta={usada}
                titulo="Esta plantilla la piden servicios"
                texto="Para archivarla, quítala antes de la ficha de cada servicio que la pide."
                cancelar="Entendido"
                cancelarIcono="check"
                accion=""
                movil={movil}
                onCerrar={() => { setUsada(false); }}
            >
                {usos.map((s) => (
                    <p key={s.id} className="rv-sv-nota">
                        <Icono nombre={iconoCategoria(s.current_version.category)} />
                        <span>{s.current_version.name}</span>
                    </p>
                ))}
            </Ventana>
        </>
    );
}

function Vista({ ops, negocio, nombreNegocio, plantilla, form }: Props & { form: FormPlantilla }) {
    const local = vistaPrevia(form.texto);
    const [api, setApi] = useState<{ texto: string; titulo: string | null; malas: string[] } | null>(null);
    useEffect(() => {
        if (plantilla === null) {
            return;
        }
        let vivo = true;
        ops.previewDocumentTemplate(negocio, plantilla.id, { body: form.texto, sample_values: { ...EJEMPLOS, 'negocio.nombre': nombreNegocio } })
            .then((r) => {
                if (vivo) {
                    setApi({ texto: r.rendered, titulo: r.title ?? null, malas: r.missing_variables ?? [] });
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, plantilla, form.texto, nombreNegocio]);
    const texto = api?.texto ?? local.texto.replaceAll(EJEMPLOS['negocio.nombre'] ?? '', nombreNegocio);
    const malas = api?.malas ?? local.malas;
    return (
        <div className="rv-sv-form" style={{ maxWidth: 640 }}>
            <p className="rv-sv-nota">Así lo leerá el cliente antes de firmar, con datos de ejemplo.</p>
            {malas.length > 0 && (
                <div role="alert" className="rv-sv-alerta">
                    <Icono nombre="triangle-alert" />
                    <span>{`«${malas[0] ?? ''}» no es un dato que se pueda rellenar: quítalo o elige uno de la lista.`}</span>
                </div>
            )}
            <div className="rv-sv-documento">
                <small>{nombreNegocio}</small>
                <strong>{api?.titulo ?? (form.titulo.trim() === '' ? 'Sin título' : form.titulo)}</strong>
                <span>{texto}</span>
                <p className="rv-sv-nota">
                    <Icono nombre="signature" />
                    <span>{lineaFirma(form)}</span>
                </p>
            </div>
        </div>
    );
}

function Versiones({ ops, negocio, plantilla }: Props & { plantilla: PlantillaDocumento }) {
    const [lista, setLista] = useState<VersionPlantilla[] | null>(null);
    useEffect(() => {
        let vivo = true;
        ops.listDocumentTemplateVersions(negocio, plantilla.id, { limit: 50 })
            .then((r) => {
                if (vivo) {
                    setLista(r.data);
                }
            })
            .catch(() => {
                if (vivo) {
                    setLista([]);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, plantilla.id, plantilla.current_version.number]);
    return (
        <div>
            {(lista ?? []).map((x) => (
                <div key={x.id} className="rv-sv-version">
                    <span className={`rv-sv-version__n${x.number === plantilla.current_version.number ? ' rv-sv-version__n--actual' : ''}`}>{x.number}</span>
                    <span>
                        <strong>{x.number === 1 ? 'Primera versión' : x.title}</strong>
                        <small>{diaMes(x.published_at)}</small>
                    </span>
                </div>
            ))}
            <p className="rv-sv-nota">Cada documento firmado guarda la versión exacta que se firmó.</p>
        </div>
    );
}
